/**
 * VDOT calculator — Jack Daniels' Running Formula.
 *
 * **Phase 6b1.5 PR 1 (2026-05-10):** this file was REPLACED. Prior
 * to this PR, `lib/vdot.ts` was a "baseline-times multiplier"
 * approximation borrowed from Claude Design's Phase 2 onboarding
 * mockup; it returned plausible but un-validated paces and carried
 * a "Phase 2.5 follow-on: replace with the proper Jack Daniels
 * VDOT formula" note in its header. That follow-on lands here.
 *
 * The Daniels port is verbatim from vanilla
 * `src/renderer/js/utils/vdot.js`. Two distinct consumers now share
 * one algorithm:
 *
 *   1. **Onboarding Step 5** (`Step5Pace.tsx`) — uses the
 *      `calcPaces(label, seconds)` shim that wraps Daniels and
 *      returns 4 zones in min/km strings. The shim preserves the
 *      pre-existing `RaceDistance` display-label union and the
 *      `PaceZone` shape so Step 5 needs only a one-line import
 *      change. New athletes see Daniels-derived paces in onboarding
 *      from PR 1 onward.
 *   2. **Settings Recalculate modal** (PR 3, not yet shipped) —
 *      will call `calculateVDOT(key, seconds)` + `trainingPaces(vdot)`
 *      directly with the canonical `RaceDistanceKey` set. Returns
 *      min/mi strings (vanilla parity; Settings persists paces as
 *      MM:SS strings on `athlete_context`).
 *
 * **Existing athletes' persisted paces are NOT recomputed.** Per
 * locked decision #2 of the Phase 6b1.5 plan, athletes whose
 * `_easy_pace` / `_vdot` were computed by the old approximation
 * keep those values until they themselves run the Settings
 * Recalculate flow (PR 3). This file's algorithm swap changes
 * onboarding *previews* for new athletes only.
 *
 * Algorithm reference: Daniels, "Daniels' Running Formula" (3rd
 * edition), tables 5.1–5.3. The percentVO2 + vo2 formulas come
 * from Daniels & Gilbert.
 */

// ─────────────────────────────────────────────────────────────
// Canonical key map (used by Settings — PR 3 consumer)
// ─────────────────────────────────────────────────────────────

/** Stable canonical race distance keys. Persisted on
 *  `athlete_context._pace_race_distance`. Lowercase, stable across
 *  vanilla and React surfaces (vanilla picker values verbatim). */
export type RaceDistanceKey =
  | '800m'
  | '1mile'
  | '3200m'
  | '3k'
  | '5k'
  | '10k'
  | 'half_marathon'
  | 'marathon';

/** Canonical key → {label, meters}. Persist the key, render the
 *  label. Includes '800m' for forward-compatibility even though the
 *  Phase 6b1.5 Recalculate modal exposes only 6 of these. */
export const RACE_DISTANCES: Record<RaceDistanceKey, {
  label: string;
  meters: number;
}> = {
  '800m':          { label: '800 m',         meters: 800 },
  '1mile':         { label: '1 Mile',        meters: 1609.34 },
  // Ruling 6 (2026-10-22): the wizard's "3200m / 2 Mile" option, a core high
  // school distance, now has a storage key so its time is saved. Same metres
  // as the server's paceResolver '3200m' and the wizard's own picker.
  '3200m':         { label: '3200m / 2 Mile', meters: 3218.69 },
  '3k':            { label: '3K',            meters: 3000 },
  '5k':            { label: '5K',            meters: 5000 },
  '10k':           { label: '10K',           meters: 10000 },
  'half_marathon': { label: 'Half Marathon', meters: 21097.5 },
  'marathon':      { label: 'Marathon',      meters: 42195 },
};

/** Distance options surfaced by the Phase 6b1.5 Recalculate modal
 *  (vanilla parity, 6 options). `800m` is reserved in the library
 *  map but intentionally not surfaced in the modal this phase —
 *  decoupled so a future enhancement can extend the modal scope
 *  without touching the library. */
export const RECALC_MODAL_DISTANCE_KEYS: readonly RaceDistanceKey[] = [
  '1mile',
  '3k',
  '5k',
  '10k',
  'half_marathon',
  'marathon',
] as const;

// ─────────────────────────────────────────────────────────────
// Daniels & Gilbert formula (internal)
// ─────────────────────────────────────────────────────────────

/** VO2max sustained percentage at a given race time (Daniels &
 *  Gilbert curve fit). */
function percentVO2AtTime(timeMinutes: number): number {
  return (
    0.8 +
    0.1894393 * Math.exp(-0.012778 * timeMinutes) +
    0.2989558 * Math.exp(-0.1932605 * timeMinutes)
  );
}

/** VO2 cost of running at a given speed (m/min). */
function vo2AtSpeed(metersPerMin: number): number {
  return -4.6 + 0.182258 * metersPerMin + 0.000104 * metersPerMin * metersPerMin;
}

/** Compute VDOT from a race result. Returns the unrounded value;
 *  the public `calculateVDOT` rounds to 1 decimal. */
function vdotFromRaceUnrounded(distMeters: number, timeSeconds: number): number {
  const t = timeSeconds / 60;
  const metersPerMin = distMeters / t;
  return vo2AtSpeed(metersPerMin) / percentVO2AtTime(t);
}

/** Daniels velocity (m/min) at a given VDOT for a training zone.
 *  The base velocity curve is Daniels' fitted polynomial; the per-
 *  zone multiplier comes from his published % VO2max bands:
 *  Easy ~72% · Long ~70% · Tempo ~88% · Interval ~98% · Race ~93%. */
function velocityForZone(vdot: number, zone: keyof DanielsPaces): number {
  const baseVelocity = 29.54 + 5.000663 * vdot - 0.007546 * vdot * vdot;
  const multipliers: Record<keyof DanielsPaces, number> = {
    easy:     0.72,
    long:     0.70,
    tempo:    0.88,
    interval: 0.98,
    race:     0.93,
  };
  return baseVelocity * multipliers[zone];
}

/** Convert velocity (m/min) to a `mm:ss` pace string in the
 *  requested unit ('mi' = min/mile, 'km' = min/kilometer). */
function paceFromVelocity(metersPerMin: number, unit: 'mi' | 'km'): string {
  const distPerUnit = unit === 'km' ? 1000 : 1609.34;
  const minPerUnit = distPerUnit / metersPerMin;
  return formatMinutes(minPerUnit);
}

// ─────────────────────────────────────────────────────────────
// Public Daniels API (used by Settings — PR 3 consumer)
// ─────────────────────────────────────────────────────────────

/** Daniels 5-zone pace set. Each value is `mm:ss` (no unit suffix —
 *  caller appends `/mi` or `/km`). */
export interface DanielsPaces {
  easy: string;
  long: string;
  tempo: string;
  interval: string;
  race: string;
}

/**
 * Calculate VDOT from a race result.
 *
 * `distance` is the canonical `RaceDistanceKey` (NOT a display label).
 * Returns 1-decimal precision (e.g. `49.8`) per vanilla
 * `utils/vdot.js:69`. Returns `null` for invalid inputs.
 *
 * Drift-guard: passing a display label like `'5K'` here returns
 * `null` rather than silently doing the wrong thing — `RACE_DISTANCES`
 * is keyed by lowercase canonical only.
 */
export function calculateVDOT(
  distance: RaceDistanceKey,
  timeSeconds: number,
): number | null {
  if (
    !RACE_DISTANCES[distance] ||
    !Number.isFinite(timeSeconds) ||
    timeSeconds <= 0
  ) {
    return null;
  }
  const distMeters = RACE_DISTANCES[distance].meters;
  const vdot = vdotFromRaceUnrounded(distMeters, timeSeconds);
  if (!Number.isFinite(vdot)) return null;
  return Math.round(vdot * 10) / 10;
}

/**
 * Compute the 5 training-zone paces at a given VDOT.
 *
 * Unit defaults to `'mi'` (vanilla parity — Settings persists paces
 * as min/mile strings). Pass `'km'` for the onboarding shim path.
 *
 * Returns `null` outside the [10, 90] VDOT band (matches vanilla
 * `utils/vdot.js:79`).
 */
export function trainingPaces(
  vdot: number,
  unit: 'mi' | 'km' = 'mi',
): DanielsPaces | null {
  if (!Number.isFinite(vdot) || vdot < 10 || vdot > 90) return null;
  return {
    easy:     paceFromVelocity(velocityForZone(vdot, 'easy'),     unit),
    long:     paceFromVelocity(velocityForZone(vdot, 'long'),     unit),
    tempo:    paceFromVelocity(velocityForZone(vdot, 'tempo'),    unit),
    interval: paceFromVelocity(velocityForZone(vdot, 'interval'), unit),
    race:     paceFromVelocity(velocityForZone(vdot, 'race'),     unit),
  };
}

/**
 * Parse a `MM:SS` or `HH:MM:SS` string into total seconds. Returns
 * `null` for malformed input. Used by:
 *
 *   - onboarding Step 5 time input (via `parseRaceTime` alias)
 *   - Settings Recalculate modal time input (PR 3)
 *
 * **Phase 6b1.5 widening:** the legacy `parseRaceTime` shim accepted
 * only `MM:SS`. Daniels' marathon times naturally exceed 60 minutes
 * and athletes type `3:45:00` — `parseTimeToSeconds` now accepts
 * both shapes. Onboarding Step 5 reads times through `parseRaceTime`
 * (below), which takes both shapes with two-digit seconds (B440).
 */
export function parseTimeToSeconds(raw: string): number | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // HH:MM:SS
  const hmsMatch = trimmed.match(/^(\d{1,3}):([0-5]?\d):([0-5]?\d)$/);
  if (hmsMatch) {
    const h = Number(hmsMatch[1]);
    const m = Number(hmsMatch[2]);
    const s = Number(hmsMatch[3]);
    return h * 3600 + m * 60 + s;
  }

  // MM:SS (seconds restricted to 00..59)
  const msMatch = trimmed.match(/^(\d{1,3}):([0-5]?\d)$/);
  if (msMatch) {
    const m = Number(msMatch[1]);
    const s = Number(msMatch[2]);
    return m * 60 + s;
  }

  // Bare digits, positional (B50): the recalc sheet's time field uses
  // inputMode="numeric", and the iOS numeric keypad HAS NO COLON KEY —
  // "3600" is the only way a phone user can type 36:00 there. 3-4
  // digits read as M(M)SS, 5-6 as H(H)MMSS; MM/SS positions must be
  // valid (< 60). 1-2 digits stay invalid — too ambiguous to guess at.
  if (/^\d{3,6}$/.test(trimmed)) {
    const sTail = Number(trimmed.slice(-2));
    if (sTail > 59) return null;
    if (trimmed.length <= 4) {
      return Number(trimmed.slice(0, -2)) * 60 + sTail;
    }
    const mMid = Number(trimmed.slice(-4, -2));
    if (mMid > 59) return null;
    return Number(trimmed.slice(0, -4)) * 3600 + mMid * 60 + sTail;
  }

  return null;
}

// ─────────────────────────────────────────────────────────────
// Display-unit conversion (B285/B287 — Settings' own paces surface)
// ─────────────────────────────────────────────────────────────

/** Meters in a mile — the same `RACE_DISTANCES['1mile'].meters` value,
 *  aliased here so the conversion functions below read standalone and
 *  match the server's own `METERS_PER_MILE` alias in
 *  `api/lib/planning/paceResolver.js` name for name. */
const METERS_PER_MILE = RACE_DISTANCES['1mile'].meters;

/** `MM:SS` from a whole number of seconds — matches the server's inline
 *  `${mins}:${secs.toString().padStart(2, '0')}` formatting exactly. Not
 *  `formatMinutes` (below), which rounds a FRACTIONAL minute value; these
 *  seconds are always already a rounded integer. */
function formatSecondsAsPace(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

/**
 * Convert a per-MILE `MM:SS` pace string to per-KM, for DISPLAY only.
 *
 * B285/B287 (founder rulings, 2026-09-27): storage never changes — every
 * writer (`RecalculateModal`'s atomic save, `usePersistOnboarding`) keeps
 * persisting `_easy_pace` et al. per mile regardless of the athlete's own
 * `distance_unit`. A km athlete's OWN Settings screen converts that stored
 * string for display, using the EXACT math the server already applies in
 * `api/lib/planning/paceResolver.js`'s `convertStoredPaceMiToKm` (same
 * `1609.34` mile-in-meters constant, same round-then-format) — so
 * Settings, the coach panel and chat never disagree by a second. Returns
 * `null` for an unparseable input (caller decides the fallback).
 */
export function convertPaceMiToKm(mmss: string): string | null {
  const totalSecPerMile = parseTimeToSeconds(mmss);
  if (totalSecPerMile === null) return null;
  const totalSecPerKm = Math.round(totalSecPerMile / (METERS_PER_MILE / 1000));
  return formatSecondsAsPace(totalSecPerKm);
}

/**
 * The inverse of `convertPaceMiToKm` — a km-space `MM:SS` pace back to the
 * per-mile canonical string every writer stores. Not called by this lane's
 * owned surfaces today: there is no direct pace-typing input (every pace
 * is derived from a race distance/time via `trainingPaces`, computed per
 * mile for storage independent of the display unit). Exported for API
 * symmetry with the server's own `convertKmToStoredMilePace` and for a
 * future direct-entry surface.
 */
export function convertPaceKmToMi(mmss: string): string | null {
  const totalSecPerKm = parseTimeToSeconds(mmss);
  if (totalSecPerKm === null) return null;
  const totalSecPerMile = Math.round(totalSecPerKm * (METERS_PER_MILE / 1000));
  return formatSecondsAsPace(totalSecPerMile);
}

/**
 * B285 fix1 — the athlete's pace display unit, read exactly as the server's
 * `resolveUnit` (`api/lib/planning/paceResolver.js`) reads it: the lowercase
 * key, then the legacy TitleCase `'Distance unit'` key, then camelCase;
 * anything other than a km spelling (B502, see `normalizeDistanceUnit`)
 * reads as mi. The client reads
 * `context_data` directly, so a unit held only in the structured column
 * (which the server merges in via `mergeContextColumns`) is not visible
 * here. Settings, the coach panel and chat then agree on the unit, not just
 * on the conversion math.
 */
export function resolveDistanceUnit(ctx: Record<string, unknown> | null | undefined): 'mi' | 'km' {
  const raw = (ctx && (ctx.distance_unit || ctx['Distance unit'] || ctx.distanceUnit)) || 'mi';
  return normalizeDistanceUnit(raw);
}

/**
 * B502 — the client mirror of `normalizeDistanceUnit` in
 * `api/lib/distance-unit.js` (the client cannot import the server module).
 * km for km, kms, kilometer(s) and kilometre(s), any case, trimmed; mi for
 * everything else, including any value that is not a string (never coerced).
 * `src/lib/vdot.b285.seam.test.ts` holds the two together over one table of
 * inputs, so a change on either side fails there.
 */
export function normalizeDistanceUnit(raw: unknown): 'mi' | 'km' {
  if (typeof raw !== 'string') return 'mi';
  return /^(?:km|kms|kilomet(?:er|re)s?)$/.test(raw.trim().toLowerCase()) ? 'km' : 'mi';
}

/**
 * Format total seconds back to `MM:SS` (or `HH:MM:SS` when >= 3600).
 *
 * **Phase 6b1.5 widening:** legacy version emitted `125:00` for 2h05m;
 * new version emits `2:05:00` once total seconds >= 3600. Onboarding
 * round-trip tests for sub-hour values are preserved (they never
 * cross the 3600 boundary). Settings Recalculate modal (PR 3)
 * displays HH:MM:SS for marathon times.
 */
export function formatRaceTime(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '';
  const sec = Math.round(totalSeconds);
  if (sec >= 3600) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────
// Onboarding compat shim (used by Step5Pace.tsx)
// ─────────────────────────────────────────────────────────────

/**
 * Display-label race distance union — kept for onboarding Step 5
 * compatibility. The onboarding answers schema stores values from
 * this union; changing the storage shape would require an answers-
 * reducer migration (out of scope this phase). Maps to the
 * canonical `RaceDistanceKey` internally via `ONBOARDING_DISTANCE_METERS`.
 */
export type RaceDistance =
  | '800m'
  | '1600m / Mile'
  | '3200m / 2 Mile'
  | '5K'
  | '10K'
  | 'Half Marathon'
  | 'Marathon';

/** Onboarding's distance picker options, exported for Step 5's
 *  `<select>` enumeration. Mirrors the pre-Phase-6b1.5 `RACE_DISTANCES`
 *  array shape — Step 5 imports this instead of `Object.keys(BASE_REFERENCE_MINUTES)`. */
export const ONBOARDING_RACE_DISTANCES: readonly RaceDistance[] = [
  '800m',
  '1600m / Mile',
  '3200m / 2 Mile',
  '5K',
  '10K',
  'Half Marathon',
  'Marathon',
] as const;

/** Distance label → meters for onboarding. Includes `3200m / 2 Mile`,
 *  a real competitive distance for high-school runners (its canonical key
 *  is `'3200m'` since ruling 6, 2026-10-22). */
const ONBOARDING_DISTANCE_METERS: Record<RaceDistance, number> = {
  '800m':            800,
  '1600m / Mile':    1609.34,
  '3200m / 2 Mile':  3218.69,
  '5K':              5000,
  '10K':             10000,
  'Half Marathon':   21097.5,
  'Marathon':        42195,
};

export type PaceZoneId = 'easy' | 'tempo' | 'interval' | 'long';

export interface PaceZone {
  id: PaceZoneId;
  label: string;
  /** Formatted as `M:SS/km` — e.g. `5:42/km`. */
  pace: string;
}

const ZONE_LABELS: Record<PaceZoneId, string> = {
  easy:     'Easy',
  tempo:    'Tempo',
  interval: 'Interval',
  long:     'Long run',
};

/**
 * Onboarding compat — calculate 4 training-zone paces from a race
 * distance + finish time in seconds.
 *
 * **PR 1 (this PR) changes the algorithm**: was a "baseline-times
 * multiplier" approximation; now wraps Daniels & Gilbert. The pace
 * numbers shift for new athletes (faster, more aggressive zones
 * for the same race time — matches Daniels' published tables).
 *
 * Returns 4 zones in onboarding's declared order: easy / tempo /
 * interval / long. Note: Daniels has 5 zones (the extra is `race`,
 * ~93% VO2max); the onboarding shim drops `race` because Step 5's
 * preview only renders 4 zones. The `race` pace is reachable via
 * `trainingPaces()` directly.
 *
 * @param distance — must be one of `RaceDistance`. Unknown distances
 *   return `null` (defensive).
 * @param timeSeconds — total race time in seconds. Must be > 0.
 */
export function calcPaces(
  distance: RaceDistance | undefined | null,
  timeSeconds: number | undefined | null,
): PaceZone[] | null {
  if (!distance || timeSeconds == null || !Number.isFinite(timeSeconds) || timeSeconds <= 0) {
    return null;
  }
  const meters = ONBOARDING_DISTANCE_METERS[distance];
  if (!meters) return null;
  const vdot = vdotFromRaceUnrounded(meters, timeSeconds);
  if (!Number.isFinite(vdot) || vdot < 10 || vdot > 90) return null;

  return [
    {
      id: 'easy',
      label: ZONE_LABELS.easy,
      pace: `${paceFromVelocity(velocityForZone(vdot, 'easy'), 'km')}/km`,
    },
    {
      id: 'tempo',
      label: ZONE_LABELS.tempo,
      pace: `${paceFromVelocity(velocityForZone(vdot, 'tempo'), 'km')}/km`,
    },
    {
      id: 'interval',
      label: ZONE_LABELS.interval,
      pace: `${paceFromVelocity(velocityForZone(vdot, 'interval'), 'km')}/km`,
    },
    {
      id: 'long',
      label: ZONE_LABELS.long,
      pace: `${paceFromVelocity(velocityForZone(vdot, 'long'), 'km')}/km`,
    },
  ];
}

/**
 * Format a decimal minute value as `M:SS`. E.g. 5.7 → `5:42`.
 * Public for back-compat (existing onboarding test imports it).
 */
export function formatMinutes(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 0) return '0:00';
  const m = Math.floor(minutes);
  const s = Math.round((minutes - m) * 60);
  // Carry over 60s rounding edge-case: 5.999 rounds to 6:00, not 5:60.
  if (s === 60) return `${m + 1}:00`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Onboarding Step 5's race-time reader (rulings 2026-10-21 item 12, B440).
 *
 * Times are `m:ss`, or `h:mm:ss` for long races, with seconds (and, in the
 * hour form, minutes) written as two digits from 00 to 59. Bare digits stay
 * positional because the iOS numeric keypad has no colon key (B50). A single
 * digit of seconds ("7:5") is ambiguous and is refused, never guessed. Zero is
 * not a time. Anything else returns `null` so the caller can say so.
 *
 * Narrower than `parseTimeToSeconds` (which the Settings sheet shares and which
 * still accepts one-digit seconds); new code outside onboarding should import
 * that one directly.
 */
export function parseRaceTime(raw: string): number | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  let seconds: number | null = null;
  const hms = trimmed.match(/^(\d{1,2}):([0-5]\d):([0-5]\d)$/);
  const ms = trimmed.match(/^(\d{1,3}):([0-5]\d)$/);
  if (hms) {
    seconds = Number(hms[1]) * 3600 + Number(hms[2]) * 60 + Number(hms[3]);
  } else if (ms) {
    seconds = Number(ms[1]) * 60 + Number(ms[2]);
  } else if (/^\d+$/.test(trimmed)) {
    seconds = parseTimeToSeconds(trimmed);
  }
  return seconds !== null && seconds > 0 ? seconds : null;
}
